import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  Modal,
  Select,
  Skeleton,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

import { formatPrice } from './money';
import { planChangeKind, planChangeTiming, startsNow } from './planChange';

/**
 * Assign, change, end and resume a student's plan, on the student's behalf.
 *
 * `billing` is the admin view of the student's plan (`getStudentBilling`).
 * What is offered follows from it: a student with no running paid plan can be
 * assigned one; a running plan can be changed (a dearer plan, or one in
 * another currency, starts now with an invoice; a cheaper one waits for the
 * period end), ended at period end, or have a pending end or change undone.
 * The student is notified of every change by the server.
 */
export function StudentPlanActions({ studentId, studentName, billing, size = 'sm' }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [assigning, setAssigning] = useState(false);
  const [changing, setChanging] = useState(false);
  const [ending, setEnding] = useState(false);

  const subscriptionId = billing.subscription_id;
  const isRunning = billing.is_paid;

  function refresh() {
    // Every admin screen shows a plan or an invoice somewhere.
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
    });
  }

  const cancel = useMutation({
    mutationFn: () => adminApi.cancelStudentSubscription(subscriptionId),
    onSuccess: (result) => {
      toast.success(result.message ?? 'The plan will end at the period end');
      setEnding(false);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not end the plan')),
  });

  const resume = useMutation({
    mutationFn: () => adminApi.resumeStudentSubscription(subscriptionId),
    onSuccess: (result) => {
      toast.success(result.message ?? 'The plan continues');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not resume the plan')),
  });

  return (
    <>
      {isRunning ? (
        <>
          <Button variant="secondary" size={size} onClick={() => setChanging(true)}>
            Change plan
          </Button>
          {billing.cancel_at_period_end || billing.scheduled_plan ? (
            <Button size={size} isLoading={resume.isPending} onClick={() => resume.mutate()}>
              {billing.scheduled_plan ? 'Cancel scheduled change' : 'Resume'}
            </Button>
          ) : (
            <Button variant="ghostDanger" size={size} onClick={() => setEnding(true)}>
              End plan
            </Button>
          )}
        </>
      ) : (
        <Button size={size} onClick={() => setAssigning(true)}>
          Assign a plan
        </Button>
      )}

      <PlanPickerModal
        open={assigning}
        mode="assign"
        studentId={studentId}
        studentName={studentName}
        billing={billing}
        onClose={() => setAssigning(false)}
        onDone={refresh}
      />
      <PlanPickerModal
        open={changing}
        mode="change"
        studentId={studentId}
        studentName={studentName}
        billing={billing}
        onClose={() => setChanging(false)}
        onDone={refresh}
      />

      <ConfirmDialog
        open={ending}
        title={`End ${studentName}'s plan`}
        message={`They keep ${billing.plan.name} until ${formatDate(
          billing.current_period_end,
        )} and move to Student Free then. Nothing is lost before that, and this can be undone until it happens. The student is notified.`}
        confirmLabel="End at period end"
        isLoading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        onCancel={() => setEnding(false)}
      />
    </>
  );
}

function PlanPickerModal({ open, mode, studentId, studentName, billing, onClose, onDone }) {
  const toast = useToast();
  const [planCode, setPlanCode] = useState('');
  const [markPaid, setMarkPaid] = useState(true);

  const plans = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.listBillingPlans,
    enabled: open,
  });

  // Paid, live student plans. Private ones are offered: an admin assigning a
  // negotiated plan is exactly what a private plan is for.
  const offered = (plans.data ?? []).filter(
    (plan) =>
      plan.audience === 'STUDENT' &&
      plan.is_active &&
      plan.price_cents > 0 &&
      (mode === 'assign' || plan.code !== billing.plan.code),
  );
  const chosen = offered.find((plan) => plan.code === planCode);
  const changeKind =
    mode === 'change' && chosen
      ? planChangeKind(chosen, {
          code: billing.plan.code,
          price_cents: billing.plan.price_cents,
          currency: billing.plan.currency,
          interval: billing.plan.interval,
          running: billing.is_paid,
        })
      : null;

  const save = useMutation({
    mutationFn: () =>
      mode === 'assign'
        ? adminApi.assignStudentPlan({ studentId, planCode, markPaid })
        : adminApi.changeStudentPlan(billing.subscription_id, planCode),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Plan updated');
      setPlanCode('');
      onClose();
      onDone();
    },
    onError: (error) =>
      toast.error(
        errorMessage(error, mode === 'assign' ? 'Could not assign the plan' : 'Could not change'),
      ),
  });

  const pending = billing.pending_invoice;

  return (
    <Modal
      open={open}
      title={mode === 'assign' ? `Put ${studentName} on a plan` : `Change ${studentName}'s plan`}
      description={
        mode === 'assign'
          ? 'Issues an invoice at the plan price. With no payment gateway the plan starts now and the invoice waits for the transfer; with one, it starts when paid.'
          : "The student's own rules: a dearer plan, or one in another currency, starts now with a new period and an invoice; a cheaper one starts when the current period ends."
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button disabled={!chosen} isLoading={save.isPending} onClick={() => save.mutate()}>
            {mode === 'assign' ? 'Assign' : 'Change plan'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {plans.isPending ? (
          <div className="space-y-2" role="status" aria-label="Loading plans">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-10" />
          </div>
        ) : plans.isError ? (
          <Alert tone="error" title="Could not load the plans">
            <p>{errorMessage(plans.error)}</p>
            <Button
              variant="secondary"
              size="xs"
              className="mt-2"
              onClick={() => void plans.refetch()}
            >
              Try again
            </Button>
          </Alert>
        ) : offered.length === 0 ? (
          <Alert tone="warning">
            There is no other paid student plan to choose. Create one under Plans first.
          </Alert>
        ) : (
          <Select
            label="Plan"
            value={planCode}
            onChange={(event) => setPlanCode(event.target.value)}
          >
            <option value="">Choose a plan</option>
            {offered.map((plan) => (
              <option key={plan.code} value={plan.code}>
                {plan.name} — {formatPrice(plan.price_cents, plan.currency, plan.interval)}
                {plan.is_public ? '' : ' (private)'}
              </option>
            ))}
          </Select>
        )}

        {changeKind && (
          <Alert tone={startsNow(changeKind) ? 'warning' : 'info'}>
            {startsNow(changeKind)
              ? `${planChangeTiming(changeKind)}. ${studentName} moves to ${chosen.name} and is invoiced ${formatPrice(chosen.price_cents, chosen.currency, chosen.interval)}.`
              : `${planChangeTiming(changeKind, billing.current_period_end)}. ${studentName} keeps ${billing.plan.name} until then; nothing is billed today.`}
          </Alert>
        )}

        {mode === 'assign' && (
          <>
            <Checkbox
              label="Mark as paid — the plan starts now"
              checked={markPaid}
              onChange={(event) => setMarkPaid(event.target.checked)}
            />
            <p className="text-ink-muted text-xs">
              {markPaid
                ? 'The invoice is recorded as paid with the reference “Assigned by admin”. Use this for a comped plan or money already received.'
                : 'The invoice waits unpaid, exactly like a request the student made: the plan starts now while no payment gateway is configured, otherwise once it is marked paid.'}
            </p>
            {pending && chosen && (
              <Alert tone={pending.plan_code === planCode && !markPaid ? 'warning' : 'info'}>
                {pending.plan_code === planCode
                  ? markPaid
                    ? `Invoice ${pending.number} for this plan is already waiting; it is the one marked paid.`
                    : `Invoice ${pending.number} for this plan is already waiting. Tick “Mark as paid” to settle it.`
                  : `Invoice ${pending.number}, waiting for another plan, is voided.`}
              </Alert>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
